import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import { useAccounts } from '../../context/AccountContext';
import ScalingModal from '../../components/ScalingModal';
import ScalingModesInfoModal from '../../components/ScalingModesInfoModal';
import Sparkline from '../../components/Sparkline';

export default function FindTraders() {
  // Each card is one followable TRADER ACCOUNT. `following` on a card lists
  // which of MY accounts already copy it, so the badge reflects the account
  // currently selected in the switcher rather than a global yes/no.
  const { selectedId, accounts } = useAccounts();
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [filters, setFilters] = useState({ minWinRate: '', maxDrawdown: '' });
  const [selected, setSelected] = useState(null);
  const [msg, setMsg]           = useState('');
  const [showScalingInfo, setShowScalingInfo] = useState(false);
  const seqRef = useRef(0);        // stale-response guard: only the newest request lands
  const debounceRef = useRef(null);

  function load(override) {
    setLoading(true);
    setError('');
    const seq = ++seqRef.current;
    const active = override || filters;
    const params = {};
    if (active.minWinRate)  params.minWinRate  = active.minWinRate;
    if (active.maxDrawdown) params.maxDrawdown = active.maxDrawdown;
    api.get('/copier/traders', { params })
      .then(r => { if (seq === seqRef.current) setTraders(r.data.traders || []); })
      .catch(e => { if (seq === seqRef.current) setError(e.response?.data?.error || 'Failed to load traders'); })
      .finally(() => { if (seq === seqRef.current) setLoading(false); });
  }

  // Typing "25" fires for "2" then "25" — debounce so only the settled value
  // hits the API, and the sequence guard drops any out-of-order response.
  function setFilter(key, val) {
    const next = { ...filters, [key]: val };
    setFilters(next);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(next), 300);
  }

  function clearFilters() {
    clearTimeout(debounceRef.current);
    const next = { minWinRate: '', maxDrawdown: '' };
    setFilters(next);
    load(next);
  }

  // Load once on mount. `load` reads current filters via closure; filter
  // changes call load() explicitly (setFilter/clearFilters), so this effect
  // intentionally has no dependencies.
  useEffect(() => {
    load();
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const WIN_RATE_SUGGESTIONS = [50, 60, 70, 80];
  const DRAWDOWN_SUGGESTIONS = [10, 20, 30, 50];
  const activeCount =
    (filters.minWinRate  ? 1 : 0) +
    (filters.maxDrawdown ? 1 : 0);

  function onSaved() {
    setSelected(null);
    setMsg('Settings saved!');
    load();
  }

  return (
    <div className="row">
      {selected && (
        <ScalingModal
          trader={selected.trader}
          existing={selected.existing}
          onClose={() => setSelected(null)}
          onSave={onSaved}
        />
      )}
      {showScalingInfo && (
        <ScalingModesInfoModal onClose={() => setShowScalingInfo(false)} />
      )}
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <h5 className="card-title mb-0">Find Traders</h5>
              <button type="button" className="btn btn-link btn-sm p-0"
                onClick={() => setShowScalingInfo(true)}>
                How scaling works
              </button>
            </div>
            {msg && <div className="alert alert-success alert-dismissible mt-2">
              {msg} <button className="close" onClick={() => setMsg('')}>&times;</button>
            </div>}
          </div>
          <div className="card-body filter-panel">
            <div className="filter-row">
              <div className="filter-group">
                <div className="filter-label">
                  <span>Min Win Rate</span>
                  <span className="filter-label-hint">% winning trades</span>
                </div>
                <div className="filter-controls">
                  <input
                    type="number"
                    inputMode="numeric"
                    className="form-control filter-input"
                    placeholder="Any"
                    value={filters.minWinRate}
                    onChange={e => setFilter('minWinRate', e.target.value)}
                  />
                  <div className="pill-row">
                    {WIN_RATE_SUGGESTIONS.map(v => (
                      <button
                        key={v}
                        type="button"
                        className={`pill ${String(filters.minWinRate) === String(v) ? 'pill-active' : ''}`}
                        onClick={() => setFilter('minWinRate',
                          String(filters.minWinRate) === String(v) ? '' : v)}
                      >
                        ≥ {v}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="filter-group">
                <div className="filter-label">
                  <span>Max Drawdown</span>
                  <span className="filter-label-hint">peak-to-trough loss</span>
                </div>
                <div className="filter-controls">
                  <input
                    type="number"
                    inputMode="numeric"
                    className="form-control filter-input"
                    placeholder="Any"
                    value={filters.maxDrawdown}
                    onChange={e => setFilter('maxDrawdown', e.target.value)}
                  />
                  <div className="pill-row">
                    {DRAWDOWN_SUGGESTIONS.map(v => (
                      <button
                        key={v}
                        type="button"
                        className={`pill ${String(filters.maxDrawdown) === String(v) ? 'pill-active' : ''}`}
                        onClick={() => setFilter('maxDrawdown',
                          String(filters.maxDrawdown) === String(v) ? '' : v)}
                      >
                        ≤ {v}%
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {activeCount > 0 && (
              <div className="filter-footer">
                <span className="text-muted small">
                  {activeCount} filter{activeCount > 1 ? 's' : ''} active · {traders.length} match{traders.length === 1 ? '' : 'es'}
                </span>
                <button className="btn btn-sm btn-link p-0 ml-2" onClick={clearFilters}>
                  Clear all
                </button>
              </div>
            )}
          </div>
          <div className="card-body">
            {error && <div className="alert alert-danger">{error}</div>}
            {loading ? <p className="text-muted">Loading traders...</p> : (
              traders.length === 0 ? <p className="text-muted">No traders found.</p> : (
                <div className="row">
                  {traders.map(t => (
                    <div key={t.id} className="col-md-4 col-lg-3 mb-3">
                      <div className="card" style={{ border: '1px solid #444' }}>
                        <div className="card-body">
                          <h6 className="text-white mb-0">
                            {t.username || t.email}
                            {t.accountLabel && (
                              <span className="text-muted small"> · {t.accountLabel}</span>
                            )}
                          </h6>
                          <p className="text-muted small mb-2">{t.mt5Server}</p>
                          <div className="trader-sparkline mb-3">
                            <div className="d-flex justify-content-between" style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 }}>
                              <span>Cumulative P&amp;L (all-time)</span>
                              <span className={
                                (t.cumulativePnlHistory?.length && t.cumulativePnlHistory[t.cumulativePnlHistory.length - 1].v >= 0)
                                  ? 'text-success' : 'text-danger'
                              }>
                                {t.cumulativePnlHistory?.length
                                  ? (() => {
                                      const v = t.cumulativePnlHistory[t.cumulativePnlHistory.length - 1].v;
                                      return v >= 0 ? `+$${v.toFixed(2)}` : `−$${Math.abs(v).toFixed(2)}`;
                                    })()
                                  : '—'}
                              </span>
                            </div>
                            <Sparkline
                              values={(t.cumulativePnlHistory || []).map(p => p.v)}
                              height={48}
                              baseline={0}
                              stroke={
                                (t.cumulativePnlHistory?.length && t.cumulativePnlHistory[t.cumulativePnlHistory.length - 1].v >= 0)
                                  ? '#00d6b4' : '#fd5d93'
                              }
                            />
                            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                              <span>{t.closedTradeCount || 0} trades on platform</span>
                              {t.joinedAt && (
                                <span>since {new Date(t.joinedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              )}
                            </div>
                            {(!t.cumulativePnlHistory || t.cumulativePnlHistory.length < 1) && (
                              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                                No closed trades yet
                              </div>
                            )}
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">Win Rate (all-time)</span>
                            <span className={`small font-weight-bold ${t.stats?.winRate >= 50 ? 'text-success' : 'text-danger'}`}>
                              {t.stats?.winRate || 0}%
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small" title="Worst peak-to-trough drop in cumulative realized P&L across all closed trades — deposit/withdrawal independent.">
                              Trade DD
                            </span>
                            <span className="small text-warning">
                              {t.tradeDrawdown?.abs > 0
                                ? (t.tradeDrawdown.pct > 0
                                    ? `−$${t.tradeDrawdown.abs.toFixed(2)} (${t.tradeDrawdown.pct.toFixed(1)}%)`
                                    : `−$${t.tradeDrawdown.abs.toFixed(2)}`)
                                : '—'}
                            </span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">P-Factor</span>
                            <span className="small text-info">{t.stats?.profitFactor || 0}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <span className="text-muted small">Total Trades</span>
                            <span className="small">{t.stats?.totalTrades || 0}</span>
                          </div>
                          <div className="d-flex justify-content-between mb-3">
                            <span className="text-muted small">Followers</span>
                            <span className="small">{t.followers || 0}</span>
                          </div>
                          {(() => {
                            // Follow state is per (my account → this trader
                            // account). With one account selected, the badge
                            // means "that account follows this trader"; on the
                            // combined view it means "any of mine does", and
                            // following again picks the account explicitly.
                            const mine = t.following || [];
                            const relevant = selectedId
                              ? mine.filter(f => f.copierAccountId === selectedId)
                              : mine;
                            const state = relevant.find(f => f.status === 'active')?.status
                                       || relevant.find(f => f.status === 'paused')?.status
                                       || null;
                            const canFollowMore = !selectedId
                              && accounts.filter(a => a.status === 'verified').length > mine.length;

                            if (state === 'active') {
                              return canFollowMore ? (
                                <button className="btn btn-sm btn-outline-success btn-block"
                                  onClick={() => setSelected({ trader: t, existing: null })}>
                                  Following — add another account
                                </button>
                              ) : (
                                <button className="btn btn-sm btn-success btn-block" disabled>
                                  <i className="tim-icons icon-check-2 mr-1" /> Following
                                </button>
                              );
                            }
                            if (state === 'paused') {
                              return (
                                <button className="btn btn-sm btn-warning btn-block"
                                  onClick={() => setSelected({ trader: t, existing: null })}>
                                  Paused — Edit
                                </button>
                              );
                            }
                            return (
                              <button className="btn btn-sm btn-primary btn-block"
                                onClick={() => setSelected({ trader: t, existing: null })}>
                                Follow
                              </button>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
